/**
 * DayCalendar — Doctor Dashboard day-grid (slot rows × doctor columns).
 * Image-5-style: 8:00 am column of slots, each cell either an appointment card or an "Add Appointment +" tile.
 *
 * Drag-and-drop reschedule:
 *   - Appointment cards are draggable (HTML5).
 *   - Empty slot tiles act as drop targets — but only within the SAME doctor column
 *     (preserves the doctor's fee + clinical context; cross-doctor drag is rejected).
 *   - Drop hits the rescheduleAPI; UI shows toast + asks parent to refresh via onRescheduled.
 */
import { useMemo, useState, useCallback } from 'react';
import { CalendarDays, FileText, GripVertical, Undo2 } from 'lucide-react';
import { cn } from '../lib/utils';
import { rescheduleAPI } from '../lib/api';
import { toast } from 'sonner';

const SLOT_MINUTES = 30; // 30-min slots; coarse enough to fit on screen, fine enough to be useful

const fmtTime = (h, m) => {
  const ap = h >= 12 ? 'pm' : 'am';
  const hh = ((h + 11) % 12) + 1;
  return `${hh}:${String(m).padStart(2, '0')} ${ap}`;
};

const parseHM = (s = '') => {
  const [h, m] = s.split(':').map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
};

// Helper — convert a doctor's day-grid drop into the new appointment_date / time_slot pair.
// `date` here is the dd/mm/yyyy display string passed in from the dashboard; the backend
// stores `appointment_date` as ISO yyyy-mm-dd so we convert via the appointment's existing date.
const isoDateForApt = (apt) => apt?.appointment_date || apt?.date || new Date().toISOString().slice(0, 10);

const DayCalendar = ({ doctors = [], appointments = [], date, onAddAppointment, statusFilter = 'all', onRescheduled }) => {
  const [selectedDoc, setSelectedDoc] = useState('all');
  const [type, setType] = useState(statusFilter);
  // Drag state — { apt, doctor_id }
  const [dragApt, setDragApt] = useState(null);
  // Highlight target slot during drag
  const [hoverSlot, setHoverSlot] = useState(null); // `${docKey}|${slotMins}`

  const filteredDoctors = useMemo(() => selectedDoc === 'all' ? doctors : doctors.filter(d => d.user_id === selectedDoc || d.id === selectedDoc), [selectedDoc, doctors]);

  // Build slot grid 8:00am → 8:00pm
  const slots = useMemo(() => {
    const arr = [];
    for (let h = 8; h < 20; h += 1) {
      for (let m = 0; m < 60; m += SLOT_MINUTES) arr.push({ h, m, label: fmtTime(h, m), key: `${h}:${m}` });
    }
    return arr;
  }, []);

  // Index appointments by doctor & slot
  const aptsByDocSlot = useMemo(() => {
    const map = new Map();
    appointments.forEach(a => {
      if (type !== 'all' && a.type !== type) return;
      const docKey = a.doctor_id || a.doctor_user_id;
      const mins = parseHM(a.time_slot || a.time);
      if (!docKey || mins == null) return;
      const slotMins = mins - (mins % SLOT_MINUTES);
      const key = `${docKey}|${slotMins}`;
      const list = map.get(key) || [];
      list.push(a);
      map.set(key, list);
    });
    return map;
  }, [appointments, type]);

  // Color tag from appointment type (matches Image 5 visual style)
  const typeColor = (t) => {
    if (t === 'follow_up' || t === 'follow-up') return { bg: '#fef3c7', text: '#92400e', border: '#fde68a' };
    if (t === 'procedure') return { bg: '#fce7f3', text: '#9d174d', border: '#fbcfe8' };
    if (t === 'emergency') return { bg: '#fee2e2', text: '#991b1b', border: '#fecaca' };
    if (t === 'new_patient') return { bg: '#dcfce7', text: '#166534', border: '#bbf7d0' };
    return { bg: '#dbeafe', text: '#1e40af', border: '#bfdbfe' }; // consultation
  };

  // ===== Drag-and-drop handlers (same-doctor only) =====
  const handleDragStart = useCallback((e, apt, docKey) => {
    setDragApt({ apt, docKey });
    try { e.dataTransfer.setData('text/plain', apt.id || ''); e.dataTransfer.effectAllowed = 'move'; } catch { /* noop */ }
  }, []);

  const handleDragEnd = useCallback(() => { setDragApt(null); setHoverSlot(null); }, []);

  const handleDragOverSlot = useCallback((e, docKey, slotMins) => {
    // Only allow drop if the dragged appointment belongs to the same doctor column
    if (!dragApt) return;
    if (dragApt.docKey !== docKey) return;
    e.preventDefault();
    try { e.dataTransfer.dropEffect = 'move'; } catch { /* noop */ }
    const key = `${docKey}|${slotMins}`;
    setHoverSlot(prev => (prev === key ? prev : key));
  }, [dragApt]);

  const handleDropSlot = useCallback(async (e, docKey, slot) => {
    e.preventDefault();
    setHoverSlot(null);
    if (!dragApt) return;
    const { apt, docKey: fromDoc } = dragApt;
    setDragApt(null);
    if (fromDoc !== docKey) {
      toast.error('Drag within the same doctor column only (doctor fees differ).');
      return;
    }
    const newTime = `${String(slot.h).padStart(2, '0')}:${String(slot.m).padStart(2, '0')}`;
    const oldTime = apt.time_slot || apt.time;
    if (newTime === oldTime) return; // same slot — no-op
    if (!apt.id) { toast.error('Cannot reschedule — appointment id missing.'); return; }
    const newDate = isoDateForApt(apt);
    try {
      await rescheduleAPI.reschedule(apt.id, {
        new_date: newDate,
        new_time: newTime,
        reason: 'Drag-and-drop reschedule from day calendar',
      });
      toast.success(`Moved ${apt.patient_name || 'appointment'} to ${fmtTime(slot.h, slot.m)}`);
      onRescheduled?.({ appointment_id: apt.id, new_date: newDate, new_time: newTime });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to reschedule');
    }
  }, [dragApt, onRescheduled]);

  // Undo the most recent reschedule for an appointment — used by FD/Billing when a drag-drop
  // was a mistake. Backend reads db.reschedule_logs and flips appointment_date/time_slot back.
  const handleUndo = useCallback(async (e, apt) => {
    e.stopPropagation();
    e.preventDefault();
    if (!apt?.id) return;
    try {
      const res = await rescheduleAPI.undo(apt.id);
      const t = res?.data?.reverted_to?.time || '';
      toast.success(`Undone — ${apt.patient_name || 'Appointment'} reverted to ${t}`);
      onRescheduled?.({ appointment_id: apt.id, undone: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to undo');
    }
  }, [onRescheduled]);

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-4" data-testid="day-calendar">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-lg border border-slate-200 bg-slate-50">
          <CalendarDays size={13} /> Date: <span className="font-semibold">{date}</span>
        </span>
        <select
          value={selectedDoc}
          onChange={e => setSelectedDoc(e.target.value)}
          className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 bg-slate-50"
          data-testid="day-calendar-doctor-filter"
        >
          <option value="all">Doctor: All</option>
          {doctors.map(d => {
            const docName = d.name || `${d.first_name || ''} ${d.last_name || ''}`.trim() || 'Doctor';
            return (
              <option key={d.user_id || d.id} value={d.user_id || d.id}>Doctor: {docName}</option>
            );
          })}
        </select>
        <select
          value={type}
          onChange={e => setType(e.target.value)}
          className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 bg-slate-50"
          data-testid="day-calendar-type-filter"
        >
          <option value="all">Type: All</option>
          <option value="consultation">Type: Consultation</option>
          <option value="follow_up">Type: Follow-up</option>
          <option value="procedure">Type: Procedure</option>
          <option value="emergency">Type: Emergency</option>
          <option value="new_patient">Type: New Patient</option>
        </select>
        <span className="ml-auto text-[10px] text-slate-400 italic hidden md:inline-flex items-center gap-1">
          <GripVertical size={11} /> Drag a card to reschedule (same doctor only)
        </span>
      </div>

      {filteredDoctors.length === 0 ? (
        <p className="py-12 text-center text-xs text-slate-400 italic">No doctors to display</p>
      ) : (
        <div className="overflow-x-auto -mx-4 px-4">
          <table className="w-full text-xs border-collapse" data-testid="day-calendar-grid">
            <thead>
              <tr>
                <th className="w-20 text-left text-[10px] uppercase text-slate-400 font-semibold py-2"></th>
                {filteredDoctors.map(d => (
                  <th key={d.user_id || d.id} className="px-2 py-2 text-center font-semibold text-slate-700 min-w-[140px]" data-testid={`doc-col-${d.user_id || d.id}`}>
                    <p>{d.name || `${d.first_name || ''} ${d.last_name || ''}`}</p>
                    <p className="text-[10px] font-normal text-slate-400">{d.department || ''}</p>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {slots.map(s => (
                <tr key={s.key} className="align-top">
                  <td className="text-[10px] text-slate-400 py-2 pr-2 whitespace-nowrap">{s.label}</td>
                  {filteredDoctors.map(d => {
                    const docKey = d.user_id || d.id;
                    const slotMins = s.h * 60 + s.m;
                    const apts = aptsByDocSlot.get(`${docKey}|${slotMins}`) || [];
                    const slotKey = `${docKey}|${slotMins}`;
                    const isHover = hoverSlot === slotKey;
                    const isValidDropTarget = !!dragApt && dragApt.docKey === docKey;
                    return (
                      <td
                        key={docKey}
                        className={cn(
                          'border-l border-slate-100 px-1 py-1 align-top transition-colors',
                          isHover && 'bg-emerald-50',
                          isValidDropTarget && !isHover && 'bg-emerald-50/30',
                        )}
                        onDragOver={(e) => handleDragOverSlot(e, docKey, slotMins)}
                        onDragLeave={() => setHoverSlot(prev => (prev === slotKey ? null : prev))}
                        onDrop={(e) => handleDropSlot(e, docKey, s)}
                        data-testid={`slot-cell-${docKey}-${s.key}`}
                      >
                        {apts.length === 0 ? (
                          <button
                            type="button"
                            onClick={() => onAddAppointment?.({ doctor_id: docKey, date, time_slot: `${String(s.h).padStart(2,'0')}:${String(s.m).padStart(2,'0')}` })}
                            className={cn(
                              'w-full h-12 border border-dashed rounded-md flex flex-col items-center justify-center text-[10px] transition',
                              isHover ? 'border-emerald-500 bg-emerald-100 text-emerald-700 font-semibold' : 'border-slate-200 hover:border-emerald-400 hover:bg-emerald-50/30 text-slate-300 hover:text-emerald-600',
                            )}
                            data-testid={`add-slot-${docKey}-${s.key}`}
                          >
                            {isHover ? 'Drop here' : <><span className="text-base leading-none">+</span><span className="text-[9px]">Add</span></>}
                          </button>
                        ) : (
                          <div className="space-y-1">
                            {apts.map((a, i) => {
                              const col = typeColor(a.type);
                              return (
                                <div
                                  key={i}
                                  draggable={!!a.id}
                                  onDragStart={(e) => handleDragStart(e, a, docKey)}
                                  onDragEnd={handleDragEnd}
                                  className={cn(
                                    'rounded-md p-1.5 border text-left cursor-grab active:cursor-grabbing transition-opacity',
                                    dragApt?.apt?.id === a.id && 'opacity-40',
                                  )}
                                  style={{ background: col.bg, borderColor: col.border, color: col.text }}
                                  title={`${a.time_slot || a.time} · ${a.patient_name || ''} — drag to a different time slot to reschedule`}
                                  data-testid={`apt-cell-${docKey}-${s.key}-${i}`}
                                >
                                  <p className="text-[10px] font-semibold flex items-center gap-1">
                                    {a.time_slot || a.time} <FileText size={10} className="opacity-50" />
                                    {a.last_rescheduled_at && (
                                      <button
                                        type="button"
                                        onClick={(e) => handleUndo(e, a)}
                                        onMouseDown={(e) => e.stopPropagation()}
                                        draggable={false}
                                        title="Undo last reschedule"
                                        className="ml-1 inline-flex items-center gap-0.5 text-[9px] px-1.5 py-0.5 rounded bg-white/60 hover:bg-white text-slate-700 font-semibold border border-slate-300/60 hover:border-slate-400 transition cursor-pointer"
                                        data-testid={`undo-reschedule-${a.id}`}
                                      >
                                        <Undo2 size={9} /> Undo
                                      </button>
                                    )}
                                    <GripVertical size={10} className="ml-auto opacity-40" />
                                  </p>
                                  <p className="text-[11px] font-bold truncate">{a.type ? a.type.replace(/_/g, ' ') : 'Visit'}</p>
                                  <p className="text-[10px] truncate">{a.patient_name || 'Patient'}</p>
                                  <p className={cn('text-[9px] mt-0.5 capitalize', a.status === 'checked_in' ? 'font-bold' : 'opacity-70')}>
                                    {a.status === 'checked_in' ? '✓ Checked in' : a.status}
                                  </p>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default DayCalendar;
